<template>
    <div fluid>
        <div v-if="wizardData" class="mb-3">
            <div class="d-flex justify-content-between align-items-center flex-wrap pt-3 pl-3">
                <div class="pb-2 pb-md-0">
                    <b>{{ wizardData.name }} - Reviews ({{ selectedLanguageLabel }})</b>
                </div>
                <div class="d-flex">
                    <img
                        v-for="l in languageOptions"
                        :key="l.code"
                        class="select-language-icon"
                        :src="`/static/common/images/flags/${l.code}.png`"
                        @click="setLanguage(l.code)"
                    />
                </div>
            </div>
            <hr />
        </div>
        <grid
            ref="grid"
            :fetchCallback="fetchCallback"
            :columnDefs="columnDefs"
            :isAutoHeight="true"
            :isAutoWidth="true"
        >
            <responsive-button @click="$bvModal.show(copyReviewModalId)" variant="primary" :icon="['fas', 'copy']"
                >Copy Review</responsive-button
            >
            <responsive-button variant="primary" @click="$bvModal.show(createReviewModalId)" :icon="['fas', 'plus']"
                >Add Review</responsive-button
            >
            <create-edit-review-modal
                :title="modalTitle"
                :modalId="createReviewModalId"
                :loading="loading"
                @submit="createEditReview"
            />
            <modal :id="copyReviewModalId" title="Copy Review" @ok.prevent="copyReviews">
                <busy-modal :show="loading" />
                <form-select
                    v-if="initialData"
                    floatingLabel="From Property"
                    v-model="selectedSource"
                    :options="propertyChoices"
                    @change="fetchReviewFromSource"
                />
                <div v-show="reviewsForCopy.length > 0">
                    <grid
                        ref="grid_copy_reviews"
                        gridTheme="ag-theme-alpine-property-setup"
                        row-selection="multiple"
                        :suppress-row-click-selection="true"
                        :column-defs="reviewsCopyColumnDefs"
                        :header-height="50"
                        :rowData="reviewsForCopy"
                        @selection-changed="handleSelectionChange"
                        :isSearch="false"
                        :isAutoHeight="true"
                        >Reviews:</grid
                    >
                </div>
            </modal>
        </grid>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../../../api.jsx';
import { RENTAL_PROPERTY_SETUP_REVIEWS_ENDPOINT } from '../../../../endpoints';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import { formatResponseError } from '../../../../utils/api';
import Grid from '../../../generic/grid/grid.vue';
import StarsCellRenderer from './stars-cell-renderer.vue';
import { newReviewObject } from '../constants';
import CreateEditReviewModal from './create-edit-review-modal.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import FormSelect from '../../../generic/form-select/form-select.vue';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import Modal from '../../../generic/modal/modal.vue';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

export default {
    components: {
        Grid,
        CreateEditReviewModal,
        ResponsiveButton,
        FormSelect,
        BusyModal,
        Modal,
        starsCellRenderer: StarsCellRenderer,
        actionsCellRenderer: ActionsCellRenderer,
    },
    inject: ['propertyId'],
    data() {
        return {
            offset: 0,
            limit: 20,
            sort: 'created',
            desc: false,
            search: '',
            languageCode: 'en',
            createReviewModalId: 'create-review-modal-id',
            copyReviewModalId: 'copy-review-modal-id',
            selectedReviews: [],
            reviewsForCopy: [],
            selectedSource: null,
            loading: false,
            newReviewObject,
        };
    },
    methods: {
        setLanguage(code) {
            this.languageCode = code;
            this.refresh();
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        async fetchReviewFromSource() {
            try {
                this.loading = true;
                const { data } = await API.get(
                    `${RENTAL_PROPERTY_SETUP_REVIEWS_ENDPOINT(this.selectedSource)}reviews-source/`,
                    {
                        params: {
                            code: this.languageCode,
                        },
                    }
                );
                this.reviewsForCopy = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch reviews' });
            } finally {
                this.loading = false;
            }
        },
        async copyReviews() {
            try {
                this.loading = true;

                const selectedReviews = this.selectedReviews.map((item) => item.id);
                await API.post(`${RENTAL_PROPERTY_SETUP_REVIEWS_ENDPOINT(this.propertyId)}copy-reviews/`, {
                    id: this.selectedSource,
                    reviews_ids: selectedReviews,
                });
                this.refresh();
                this.$bvModal.hide(this.copyReviewModalId);
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not copy review' });
            } finally {
                this.loading = false;
            }
        },
        async createEditReview(values, modalId) {
            try {
                this.loading = true;

                const language = this.languageOptions.find((item) => item.code === this.languageCode)?.language_id;

                await API.post(`${RENTAL_PROPERTY_SETUP_REVIEWS_ENDPOINT(this.propertyId)}save/`, {
                    ...values,
                    language,
                    rental_property: this.propertyId,
                    date: format(parseISO(values.date), 'yyyy-MM-dd'),
                    airbnb_review_id: values.airbnb_review_id || '',
                });
                this.refresh();
                this.$bvModal.hide(modalId);
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not save review' });
            } finally {
                this.loading = false;
            }
        },
        handleSelectionChange() {
            const gridApi = this.$refs.grid_copy_reviews.gridOptions.api;
            this.selectedReviews = gridApi.getSelectedRows();
        },
    },
    computed: {
        ...mapGetters('setupPropertyStore', ['wizardData', 'initialData', 'propertyChoices']),
        languageOptions() {
            const englishLanguage = { id: null, code: 'en', name: 'English' };
            return this.initialData ? [englishLanguage, ...this.initialData.active_languages] : [englishLanguage];
        },
        selectedLanguageLabel() {
            return this.languageOptions.find((item) => item.code === this.languageCode)?.name;
        },
        modalTitle() {
            return `Add Review (${this.selectedLanguageLabel})`;
        },
        columnDefs() {
            return [
                {
                    field: 'date',
                    headerName: 'Date',
                    type: 'dateColumn',
                    sort: 'desc',
                    minWidth: 150,
                    maxWidth: 160,
                },
                {
                    field: 'name',
                    headerName: 'Name',
                    minWidth: 200,
                    autoWidthExclude: true,
                },
                {
                    field: 'source',
                    headerName: 'Source',
                },
                {
                    field: 'stars',
                    headerName: 'Stars',
                    minWidth: 140,
                    maxWidth: 140,
                    cellRenderer: 'starsCellRenderer',
                },
                {
                    field: 'title',
                    headerName: 'Title',
                    minWidth: 200,
                    autoWidthExclude: true,
                },
                {
                    field: 'description',
                    headerName: 'Review',
                    minWidth: 200,
                    autoWidthExclude: true,
                },
                {
                    field: '',
                    minWidth: 150,
                    maxWidth: 150,
                    sortable: false,
                    resizable: false,
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        language: this.selectedLanguageLabel,
                        onSave: async (values, modalId) => await this.createEditReview(values, modalId),
                        onRemove: async (id, modalId) => {
                            try {
                                await API.post(`${RENTAL_PROPERTY_SETUP_REVIEWS_ENDPOINT(this.propertyId)}remove/`, {
                                    id,
                                });
                                await this.refresh();
                                this.$bvModal.hide(modalId);
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({
                                    text: generic || 'Could not delete review',
                                });
                            }
                        },
                    },
                },
            ];
        },
        reviewsCopyColumnDefs() {
            return [
                {
                    field: 'name',
                    headerName: 'Name',
                    headerCheckboxSelection: true,
                    headerCheckboxSelectionFilteredOnly: true,
                    checkboxSelection: true,
                },
                {
                    field: 'stars',
                    headerName: 'Stars',
                },
            ];
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(`${RENTAL_PROPERTY_SETUP_REVIEWS_ENDPOINT(this.propertyId)}grid/`, {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                        language_code: this.languageCode,
                    },
                });
            };
        },
    },
};
</script>


<style lang="scss" scoped>
.select-language-icon {
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
    cursor: pointer;
}
</style>